import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthStore } from "../../store/authStore";
import LoadingSpinner from "../ui/LoadingSpinner";

// Routes that university_admin can visit even without an active subscription
const BILLING_EXEMPT = ["/select-plan", "/billing/checkout", "/billing", "/logout", "/verify-email"];

const ProtectedRoute = ({
  children,
  allowedRoles = [],
  redirectTo = "/login",
  requireAuth = true
}) => {
  const { t } = useTranslation("auth");
  const user = useAuthStore((s) => s.user);
  // Bootstrap-only: don't gate on the store's `loading` (also true during
  // login/register/etc), or a failed submit on this route unmounts + fresh-
  // remounts whatever's rendered under it, wiping its local state.
  const loading = useAuthStore((s) => s.initializing);
  const needsBilling = useAuthStore((s) => s.needsBilling);
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <LoadingSpinner
        type="circular"
        fullScreen
        text={t("guard.authenticating")}
      />
    );
  }

  // If authentication required but user not logged in
  if (requireAuth && !user) {
    return <Navigate to={redirectTo} replace />;
  }

  // If user already logged in but accessing login/register pages
  if (!requireAuth && user) {
    // university_admin with no subscription goes to plan selection
    if (user.role === "university_admin" && needsBilling) {
      return <Navigate to="/select-plan" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Billing gate — university_admin must select a plan before accessing the app
  if (
    requireAuth &&
    user?.role === "university_admin" &&
    needsBilling &&
    !BILLING_EXEMPT.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/select-plan" replace />;
  }

  // Role-based access control
  if (user && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    switch (user.role) {
      case "student":
        return <Navigate to="/dashboard" replace />;
      case "teacher":
        return <Navigate to="/dashboard" replace />;
      case "university_admin":
        return <Navigate to="/dashboard" replace />;
      case "super_admin":
        return <Navigate to="/dashboard" replace />;
      default:
        return <Navigate to="/unauthorized" replace />;
    }
  }

  return children || <Outlet />;
};


/**
 * Higher-order component wrapper
 */

export const withProtection = (WrappedComponent, options = {}) => {

  const { allowedRoles = [], redirectTo = "/login" } = options;

  return function WithProtectionWrapper(props) {

    const { t } = useTranslation("auth");
    const user = useAuthStore((s) => s.user);
    const loading = useAuthStore((s) => s.initializing);

    if (loading) {

      return (
        <LoadingSpinner
          type="circular"
          fullScreen
          text={t("guard.authenticating")}
        />
      );

    }

    if (!user) {
      return <Navigate to={redirectTo} replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }

    return <WrappedComponent {...props} />;

  };

};


/**
 * Unauthorized page
 */

export const Unauthorized = () => {

  const { t } = useTranslation("auth");
  const user = useAuthStore((s) => s.user);

  return (

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        textAlign: "center",
        padding: "20px"
      }}
    >

      <h1 style={{ fontSize: "2rem", marginBottom: "1rem" }}>
        {t("guard.unauthorizedTitle")}
      </h1>

      <p style={{ fontSize: "1.1rem", marginBottom: "2rem", color: "#666" }}>
        {t("guard.noPermission")}
      </p>

      {user ? (

        <p>
          {t("guard.roleLacksPermission", { role: user.role })}
          <br />
          {t("guard.contactAdmin")}
        </p>

      ) : (

        <p>{t("guard.pleaseLogIn")}</p>

      )}

      <button
        onClick={() => window.history.back()}
        style={{
          padding: "10px 20px",
          fontSize: "1rem",
          backgroundColor: "#1976d2",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          marginTop: "20px"
        }}
      >
        {t("guard.goBack")}
      </button>

    </div>

  );

};

export default ProtectedRoute;
